import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import {
  objectFitCoverImageStyles,
  SDUIImage,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Image/SDUIImage";
import { SDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
// eslint-disable-next-line import/no-unassigned-import
import "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/__styles__/sduiAnimations.tw.css";
import { numberToPixel } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/conversions";
import { getCssAlignmentValue } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/getCssAlignmentValue";
import { getOpacityHexValue } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/getOpacityHexValue";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUITypographyRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { IOptimizeImageOptions } from "@faire/web--source/common/images/optimizeImages";
import { cn } from "@faire/web--source/tools/cn";
import { Alignment } from "@faire/web-api--source/indigofair/data/Alignment";
import { IImageElement } from "@faire/web-api--source/indigofair/data/IImageElement";
import { IImageTile } from "@faire/web-api--source/indigofair/data/IImageTile";
import { IOverlayElement } from "@faire/web-api--source/indigofair/data/IOverlayElement";
import React from "react";

type ImageStyles = {
  aspectRatio?: number;
  width?: string;
  height?: string;
  isCircular?: boolean;
  isCornersRounded?: boolean;
};

export interface ISDUIImageElementRendererProps {
  className?: string;
  imageElement: IImageElement;
  loggingData: ComponentLoggingEventParams;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  imageStyles?: ImageStyles;
  // TODO(FD-250644): support responsive versions
  optimizeImageOptions?: IOptimizeImageOptions;
  animations?: Array<keyof typeof IImageTile.ImageTileAnimation>;
}

export const SDUIImageElementRenderer = ({
  className,
  imageElement,
  loggingData,
  renderBehaviour,
  imageStyles,
  optimizeImageOptions,
  animations,
}: ISDUIImageElementRendererProps) => {
  const { image, on_click, overlay } = imageElement;
  if (!image) {
    return null;
  }

  const varAspectRatio: DynamicVariable<"--f-sdui-ie-aspect-ratio"> = {
    variable: "--f-sdui-ie-aspect-ratio",
    className: "aspect-(--f-sdui-ie-aspect-ratio)",
  };

  const varContentWidth: DynamicVariable<"--f-sdui-ie-content-width"> = {
    variable: "--f-sdui-ie-content-width",
    className: "w-(--f-sdui-ie-content-width)",
  };

  const varContentHeight: DynamicVariable<"--f-sdui-ie-content-height"> = {
    variable: "--f-sdui-ie-content-height",
    className: "h-(--f-sdui-ie-content-height)",
  };

  const contentWidth =
    imageStyles?.width ??
    numberToPixel(optimizeImageOptions?.width) ??
    numberToPixel(image.width) ??
    "auto";
  const contentHeight =
    imageStyles?.height ??
    numberToPixel(optimizeImageOptions?.height) ??
    numberToPixel(image.height) ??
    "auto";

  const { alt_text } = image;

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logSDUIClickEvent={on_click.length > 0}
      // we use legacy LE for product image elements on insights tile
      // https://faire-wholesale.slack.com/archives/C02QZ5FD54M/p1739389620877959?thread_ts=1739315224.894189&cid=C02QZ5FD54M
      logLegacyClickEvent={on_click.length > 0}
      className={cn(
        className,
        "h-full w-full no-underline",
        animations?.includes(IImageTile.ImageTileAnimation.ZOOM)
          ? "animate-image-zoom-target"
          : null,
        animations?.includes(IImageTile.ImageTileAnimation.UNDERLINE)
          ? "animate-underline-text-target"
          : null
      )}
      componentStyle={{
        width: contentWidth,
      }}
    >
      <div
        className={cn(
          "relative overflow-hidden",
          varContentWidth.className,
          varContentHeight.className,
          imageStyles?.isCircular ? "rounded-[50%] object-cover" : null,
          imageStyles?.isCornersRounded ? "rounded-sm object-cover" : null
        )}
        style={assignInlineVars({
          [varContentWidth.variable]: contentWidth,
          [varContentHeight.variable]: contentHeight,
          [varAspectRatio.variable]:
            imageStyles?.aspectRatio?.toString() ?? "auto",
        })}
      >
        <SDUIImage
          altText={alt_text || overlay?.text?.text}
          renderBehaviour={renderBehaviour}
          responsiveImage={{
            mobileAndAbove: image,
          }}
          optimizeImageOptions={optimizeImageOptions ?? {}}
          className={cn(
            "z-2 flex object-cover",
            varAspectRatio.className,
            varContentWidth.className,
            varContentHeight.className,
            "animate-image-zoom"
          )}
          imageStyles={objectFitCoverImageStyles}
        />
        <SDUIOverlayRenderer overlay={overlay} />
      </div>
    </SDUIOnClickHandler>
  );
};

const SDUIOverlayRenderer = ({ overlay }: { overlay?: IOverlayElement }) => {
  if (!overlay) {
    return null;
  }
  return (
    <div
      className="absolute top-0 left-0 z-3 flex h-full w-full overflow-hidden p-4"
      style={{
        justifyContent: getCssAlignmentValue(
          overlay.style?.horizontal_text_alignment
        ),
        alignItems: getCssAlignmentValue(
          overlay.style?.vertical_text_alignment ?? Alignment.ALIGNMENT_END
        ),
        backgroundColor: overlay?.color
          ? `${getSlateColorToken(overlay?.color)}${getOpacityHexValue(
              overlay?.opacity
            )}`
          : undefined,
      }}
    >
      {overlay.text ? (
        <SDUITypographyRenderer
          textElement={overlay.text}
          loggingData={undefined}
          // TODO(FD-237289) move this logic to backend
          avoidUnderLineOnHover={true}
        />
      ) : null}
    </div>
  );
};
